import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';

export default function ResetPassword() {
  const t = useT();
  const [sp] = useSearchParams();
  const tokenFromLink = sp.get('token');
  const mode: 'link' | 'otp' = tokenFromLink ? 'link' : (sp.get('mode') === 'otp' ? 'otp' : 'link');

  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [code, setCode] = useState('');
  const [identifier, setIdentifier] = useState(sp.get('identifier') || '');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      if (mode === 'link') {
        await api.post('/auth/reset-password', { token: tokenFromLink, password });
      } else {
        await api.post('/auth/reset-password-otp', { identifier, code, password });
      }
      setDone(true);
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setLoading(false); }
  }

  if (done) {
    return (
      <div className="auth-shell">
        <div className="auth-card text-center">
          <div className="auth-head">
            <div className="auth-head-badge">
              <Icon name="task_alt" size={22} />
            </div>
            <h1 className="auth-h1">{t('reset.successTitle')}</h1>
            <p className="auth-sub">{t('reset.successSub')}</p>
          </div>
          <Link to="/login" className="auth-cta inline-flex">
            {t('common.signIn')} <Icon name="arrow_forward" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-head">
          <div className="auth-head-badge">
            <Icon name="lock_reset" size={22} />
          </div>
          <h1 className="auth-h1">{t('reset.title')}</h1>
          <p className="auth-sub">{mode === 'otp' ? t('reset.subOtp') : t('reset.subLink')}</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === 'otp' && (
            <>
              <div>
                <label className="label" htmlFor="rp-id">{t('forgot.idLabel')}</label>
                <div className="auth-field">
                  <Icon name="person_search" className="auth-field-icon" />
                  <input
                    id="rp-id"
                    className="auth-input"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder={t('forgot.idPlaceholder')}
                    maxLength={120}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div>
                <label className="label" htmlFor="rp-otp">{t('reset.otpLabel')}</label>
                <div className="auth-field">
                  <Icon name="pin" className="auth-field-icon" />
                  <input
                    id="rp-otp"
                    className="auth-input tracking-[0.6em] text-center font-mono"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="• • • • • •"
                    required
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="label" htmlFor="rp-pwd">{t('reset.newPasswordLabel')}</label>
            <div className="auth-field">
              <Icon name="lock" className="auth-field-icon" />
              <input
                id="rp-pwd"
                className="auth-input"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('register.passwordPlaceholder')}
                autoComplete="new-password"
                minLength={8}
                maxLength={72}
                required
              />
              <button
                type="button"
                className="auth-field-trail min-w-[44px] min-h-[44px] grid place-items-center"
                aria-label={showPwd ? t('common.hide') : t('common.show')}
                onClick={() => setShowPwd((v) => !v)}
              >
                <Icon name={showPwd ? 'visibility_off' : 'visibility'} />
              </button>
            </div>
          </div>

          {err && <div className="auth-error" role="alert">{err}</div>}

          <button type="submit" className="auth-cta mt-1" disabled={loading}>
            {loading ? (
              <>
                <Icon name="progress_activity" className="animate-spin" />
                {t('reset.updating')}
              </>
            ) : (
              <>{t('reset.cta')} <Icon name="arrow_forward" /></>
            )}
          </button>
        </form>

        <div className="auth-foot">
          <Link to="/login" className="auth-link">← {t('forgot.back')}</Link>
        </div>
      </div>
    </div>
  );
}
